import React, { useContext } from "react";
import { graphql, useFragment } from "react-relay";

import { faArrowFromLeft, faEdit, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faHashtag } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { commitMutation } from "relay-runtime";

import { buildTagSearchUrl } from "~/lib/prose/nodes/tag";
import { environment } from "~/models";
import { HomePanelContext } from "~/nav/panels/Home";
import HomePanelGroupsMenu from "~/stream/HomePanelGroupsMenu";
import { launchEditTagModal } from "~/tags";
import IconButton from "~/ui/legacy/IconButton";
import Menu from "~/ui/menu";

import { emitter, HOME_PANEL_ITEM_REMOVED_EVENT_KEY } from "./GroupsMenu";
import MenuLink from "./MenuLink";

import type { TagLink_tag$key } from "~/__generated__/TagLink_tag.graphql";
import type { TagLinkRemoveItemMutation as TagLinkRemoveItemMutationType } from "~/__generated__/TagLinkRemoveItemMutation.graphql";

import styles from "./TagLink.module.css";

const HomePanelTagsFragment = graphql`
	fragment TagLink_tag on Tag {
		id
		label
		color
		homePanelGroup {
			id
		}
		...HomePanelGroupsMenu_object
	}
`;

interface TagLinkProps extends React.HTMLProps<HTMLDivElement> {
	tag: TagLink_tag$key;
}

const TagLink = React.forwardRef<HTMLDivElement, TagLinkProps>(({ tag, ...rest }, ref) => {
	const tagData = useFragment<TagLink_tag$key>(HomePanelTagsFragment, tag);
	const panelContext = useContext(HomePanelContext);

	const { id, label, color, homePanelGroup } = tagData;

	const icon = (
		<FontAwesomeIcon
			icon={faHashtag}
			fixedWidth
			style={{
				color: `var(--tag-${color.toLowerCase()})`,
			}}
		/>
	);

	const [isMenuOpen, setIsMenuOpen] = React.useState(false);

	const toggleOpen = React.useCallback((open: boolean) => setIsMenuOpen(open), []);

	const handleRemove = React.useCallback(() => {
		const TagLinkRemoveItemMutation = graphql`
			mutation TagLinkRemoveItemMutation($id: ID!) {
				removeObjectFromHomePanel(objectId: $id) {
					removedFromGroup {
						...HomePanelGroup_group
					}
					removedObject {
						id
						... on Tag {
							homePanelGroup {
								id
								...HomePanelGroup_group
							}
						}
					}
				}
			}
		`;

		commitMutation<TagLinkRemoveItemMutationType>(environment, {
			mutation: TagLinkRemoveItemMutation,
			variables: {
				id: id,
			},
			onCompleted: () => emitter.emit(HOME_PANEL_ITEM_REMOVED_EVENT_KEY),
		});
	}, [id]);

	const launchTagModal = React.useCallback(
		() => launchEditTagModal({ tagId: id, startingLabel: label, startingColor: color }),
		[color, id, label],
	);

	return (
		<div ref={ref} className={styles.wrapper} {...rest}>
			<MenuLink to={buildTagSearchUrl(`#${label}`)} icon={icon} iconTooltip="Tag">
				{label}
			</MenuLink>
			<Menu open={isMenuOpen} onOpenChange={toggleOpen}>
				<Menu.Trigger>
					<IconButton
						title="Options"
						icon={faEllipsisV}
						className={cx(styles.menuButton, { [styles.menuButtonVisible]: isMenuOpen })}
					/>
				</Menu.Trigger>
				<Menu.Content align="end">
					<Menu.Sub>
						<Menu.SubTrigger icon={faArrowFromLeft}>
							{homePanelGroup?.id ? "Move to group" : "Add to group"}
						</Menu.SubTrigger>
						<Menu.SubContent>
							{panelContext.viewer ? <HomePanelGroupsMenu object={tagData} viewer={panelContext.viewer} /> : null}
						</Menu.SubContent>
					</Menu.Sub>
					<Menu.Item icon={faEdit} onSelect={launchTagModal}>
						Edit…
					</Menu.Item>
					<Menu.Item icon={faTimes} onSelect={handleRemove}>
						Remove from home panel
					</Menu.Item>
				</Menu.Content>
			</Menu>
		</div>
	);
});
TagLink.displayName = "TagLink";

export default TagLink;
